import { ComboBox } from 'src/components/ui/combobox';
import { DEALER_ADMIN } from 'src/utils/enums/user-store-actions.enum';
import { DealerManagementService } from 'src/services/dealer-management.service';
import EditUser from 'src/views/pages/manageUser/EditUser/EditUser';
import FooterPrivacy from '../../../auth/components/baseTemplate/FooterPrivacy';
import { getBrandByDealerNumber } from 'src/utils/helpers';
import { IUser } from 'src/utils/interfaces/user';
import { LoginService } from 'src/services/login.service';
import NewUser from 'src/views/pages/manageUser/NewUser/NewUser';
import Spinner from 'src/components/ui/spinner';
import {
    DealerAssociation,
    DealerPermission,
} from 'src/services/models/dealer-management.service';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';

import { NUMBER_ZERO } from 'src/utils/constants';
import { Table } from './components/table';
import TableFooter from 'src/components/table/pagination/tableFooter';
import { Types } from '../dealer-security-maintenance';
import { useLocalization } from 'src/utils/useLocalization';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './styles.scss';

interface IPropsTableItem {
    FIRSTNAME: string;
    LASTNAME: string;
    LOGIN: string;
    PARTYID: number;
    PERMISSIONEXISTS: number;
    STATUS: number;
    DEALERIDS: string;
    STATUSTEXT: string;
    ID: string;
}

export enum actionTypes {
    Delete = 'Delete',
    Migrate = 'Migrate',
}

const TYPE_FILTER_OPTIONS: string[] = [actionTypes.Delete, actionTypes.Migrate];

const UserSearch = () => {
    const [displayedContent, setDisplayedContent] = useState<IPropsTableItem[]>(
        [],
    );
    const [typeFilter, setTypeFilter] = useState<string>('');
    const [dealerId, setDealerId] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isAdding, setIsAdding] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [userNameEdit, setUserNameEdit] = useState<string>('');
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [isSuccessDeactivate, setIsSuccessDeactivate] =
        useState<boolean>(false);
    const [isSuccessActivate, setIsSuccessActivate] = useState<boolean>(false);
    const [isSuccessDeleted, setIsSuccessDeleted] = useState<boolean>(false);
    const [isSuccessDeletedMFA, setIsSuccessDeletedMFA] =
        useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [isErrorDGM, setIsErrorDGM] = useState<boolean>(false);
    const [brand, setBrand] = useState<string>('vw6');
    const [dealerPermissions, setDealerPermissions] = useState<
        DealerPermission[]
    >([]);
    const [dealerAssociations, setDealerAssociations] = useState<
        DealerAssociation[]
    >([]);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();

    const { dealerMgmt, addNewUser, editUser, deleteUser } = useLocalization();

    const dsmSearch = useSelector((reduxState: IUser) => reduxState.dsmSearch);
    const currentDealers = useSelector(
        (reduxState: IUser) => reduxState.currentDealers,
    );
    const dealerDataInfo = useSelector(
        (reduxState: IUser) => reduxState.dealerDataInfo,
    );
    const permissionsToken = useSelector(
        (reduxState: IUser) => reduxState.permissionsToken,
    );
    const accessUser = useSelector(
        (reduxState: IUser) => reduxState.accessUser,
    );

    const buttonClasses = useMemo(() => `c-btn ${brand} u-ml-small`, [brand]);

    const getDealerProfileData = useCallback(async () => {
        const loginService = new LoginService();

        try {
            setIsLoading(true);

            const tokenRes = await loginService.getDealerProfile(
                permissionsToken,
            );

            const userData = tokenRes?.data?.userProfile?.userData;

            if (userData) {
                setDealerPermissions(userData.userPermissions);
                setDealerAssociations(userData.associations);
            }
        } catch {
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    }, [permissionsToken]);

    const getDealersBySearchType = useCallback(async () => {
        const service = new DealerManagementService();

        try {
            const res = await service.getDealersBySearchType(
                dsmSearch.searchType,
                dsmSearch.searchValue,
            );

            if (res?.data) {
                dispatch({
                    type: DEALER_ADMIN.DEALER_ADMIN_USER_SEARCH,
                    payload: {
                        searchType: dsmSearch.searchType,
                        searchValue: dsmSearch.searchValue,
                        users: res.data,
                    },
                });
            }
        } catch {
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, dsmSearch.searchType, dsmSearch.searchValue]);

    const getDealerGroups = useCallback(async () => {
        const service = new DealerManagementService();

        try {
            setIsLoading(true);

            const res = await service.getDealerGroups(
                isAdding ? dsmSearch.searchValue : dealerId,
            );

            if (res?.data) {
                dispatch({
                    type: DEALER_ADMIN.SET_DEALER_GROUP_DATA,
                    payload: {
                        dealerGroupState: res.data,
                    },
                });
            }
        } catch {
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    }, [dealerId, dispatch, dsmSearch.searchValue, isAdding]);

    useEffect(() => {
        if (
            dsmSearch.searchType !== Types.DealerNumber &&
            !dsmSearch.users.length
        ) {
            navigate('/dashboard/systemadmin/dealer-security-maintenance');
        }

        getDealerProfileData();

        dispatch({
            type: DEALER_ADMIN.DEALER_ADMIN_USER_ADD_SELECTED,
            payload: {
                selectedUsers: [],
            },
        });
    }, []);

    useEffect(() => {
        if (!typeFilter.length) return;

        if (typeFilter === actionTypes.Delete) {
            dispatch({
                type: DEALER_ADMIN.DEALER_ADMIN_USER_DELETE,
                payload: {},
            });

            navigate(
                '/dashboard/systemadmin/dealer-security-maintenance/delete',
            );

            return;
        }

        if (typeFilter === actionTypes.Migrate) {
            dispatch({
                type: DEALER_ADMIN.DEALER_ADMIN_USER_MIGRATION,
                payload: {},
            });

            navigate(
                '/dashboard/systemadmin/dealer-security-maintenance/migrate',
            );
        }
    }, [dispatch, navigate, typeFilter]);

    useEffect(() => {
        if (!currentDealers || !dealerDataInfo) return;

        const { dealer } = dealerDataInfo;
        const dealerBrand = getBrandByDealerNumber(
            dealer.brand,
            dealer.dealerNumber,
        );

        setBrand(dealerBrand);
    }, [currentDealers, dealerDataInfo]);

    useEffect(() => {
        if (!isSuccess) return;

        setIsLoading(true);
        getDealersBySearchType();
    }, [getDealersBySearchType, isSuccess]);

    useEffect(() => {
        if (!isAdding && !isEditing) return;

        getDealerGroups();
    }, [getDealerGroups, isAdding, isEditing]);

    const handleClickItem = useCallback(
        (userName: string, dealerIds?: string) => {
            setUserNameEdit(userName);

            if (dealerIds) {
                setDealerId(dealerIds.split(',')[0]);
            }

            setIsEditing(true);
        },
        [],
    );

    const handlerEditBackclick = useCallback(
        (didEdit: boolean, shouldLoadList?: boolean) => {
            setIsEditing(didEdit);

            if (shouldLoadList) {
                setIsLoading(true);
                getDealersBySearchType();
            }
        },
        [getDealersBySearchType],
    );

    const selectedView = useMemo(() => {
        if (
            isAdding &&
            state?.permissions &&
            state?.associations &&
            !isLoading
        ) {
            return (
                <NewUser
                    backClick={setIsAdding}
                    setSuccessNotification={setIsSuccess}
                    dealerPermissions={state.permissions}
                    dealerAssociations={state.associations}
                    isDSMSearch={true}
                />
            );
        }

        if (isEditing && !isLoading) {
            return (
                <EditUser
                    backClick={handlerEditBackclick}
                    username={userNameEdit}
                    showOnlyDeleteOption={accessUser}
                    setErrorNotification={setIsError}
                    dealerPermissions={dealerPermissions}
                    dealerAssociations={dealerAssociations}
                    setIsSuccessDeleted={setIsSuccessDeleted}
                    isDSMSearch={true}
                />
            );
        }

        return null;
    }, [
        accessUser,
        dealerAssociations,
        dealerPermissions,
        handlerEditBackclick,
        isAdding,
        isEditing,
        isLoading,
        state,
        userNameEdit,
    ]);

    if (selectedView) {
        return <>{selectedView}</>;
    }

    return (
        <div className='user-search-dsm'>
            {isSuccess && (
                <div className='success-notification'>
                    <Notification
                        content={addNewUser.NOTIFICATION_SUCCESS}
                        type={NotificationType.SUCCESS}
                        visibility={isSuccess}
                        setVisibility={setIsSuccess}
                    />
                </div>
            )}

            {isSuccessDeactivate && (
                <div className='success-notification'>
                    <Notification
                        content={editUser.NOTIFICATION_SUCCESS_DEACTIVATION}
                        type={NotificationType.SUCCESS}
                        visibility={isSuccessDeactivate}
                        setVisibility={setIsSuccessDeactivate}
                    />
                </div>
            )}

            {isSuccessActivate && (
                <div className='success-notification'>
                    <Notification
                        content={editUser.NOTIFICATION_SUCCESS_ACTIVATION}
                        type={NotificationType.SUCCESS}
                        visibility={isSuccessActivate}
                        setVisibility={setIsSuccessActivate}
                    />
                </div>
            )}

            {isSuccessDeleted && (
                <div className='success-notification'>
                    <Notification
                        content={deleteUser.NOTIFICATION_SUCCESS_DELETION}
                        type={NotificationType.SUCCESS}
                        visibility={isSuccessDeleted}
                        setVisibility={setIsSuccessDeleted}
                    />
                </div>
            )}

            {isSuccessDeletedMFA && (
                <div className='success-notification'>
                    <Notification
                        content={
                            dealerMgmt.DEALER_MGMT_TABLE_USER_MESSAGES_SUCCESSMFA_DELETED
                        }
                        type={NotificationType.SUCCESS}
                        visibility={isSuccessDeletedMFA}
                        setVisibility={setIsSuccessDeletedMFA}
                    />
                </div>
            )}

            {isError && (
                <div className='success-notification'>
                    <Notification
                        content={editUser.NOTIFICATION_ERROR_REQUEST}
                        type={NotificationType.ERROR}
                        visibility={isError}
                        setVisibility={setIsError}
                    />
                </div>
            )}

            {isErrorDGM && (
                <div className='success-notification'>
                    <Notification
                        content='Something went wrong. Please try again later.'
                        type={NotificationType.ERROR}
                        visibility={isErrorDGM}
                        setVisibility={setIsErrorDGM}
                    />
                </div>
            )}

            <div className='page-title'>
                <h5>
                    {dsmSearch.searchType === Types.DealerNumber
                        ? 'Dealer Number Search Results'
                        : 'User Search Results'}
                </h5>
            </div>

            <div
                className='page-title'
                style={{
                    alignItems: 'center',
                }}
            >
                <div>
                    <span
                        style={{
                            color: '#777777',
                        }}
                    >
                        {'< '}
                    </span>
                    <Link to='/dashboard/systemadmin/dealer-security-maintenance'>
                        <span>New Search</span>
                    </Link>
                </div>

                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-around',
                        alignItems: 'center',
                    }}
                >
                    <div className='select-ph-type'>
                        <ComboBox
                            name='type'
                            options={TYPE_FILTER_OPTIONS}
                            defaultValue={TYPE_FILTER_OPTIONS[0]}
                            placeholder='With selected...'
                            onSelect={setTypeFilter}
                            selectedOption={typeFilter}
                            customClass='xxmedium'
                            disabled={!dsmSearch.selectedUsers?.length}
                        />
                    </div>

                    {dsmSearch.searchType === Types.DealerNumber && (
                        <button
                            className={buttonClasses}
                            onClick={() => setIsAdding(true)}
                        >
                            <span className='c-btn__text'>
                                {dealerMgmt.DEALER_MGMT_ADD_BUTTON_LABEL}
                            </span>
                        </button>
                    )}
                </div>
            </div>

            <div className={`main-container ${isLoading ? 'no-content' : ''}`}>
                {isLoading ? (
                    <div className='loader_container'>
                        <Spinner />
                    </div>
                ) : dsmSearch?.users?.length > NUMBER_ZERO ? (
                    <>
                        <Table
                            items={displayedContent}
                            onClick={handleClickItem}
                            updateData={getDealersBySearchType}
                            setIsSuccessDeactivate={setIsSuccessDeactivate}
                            setIsSuccessDeleted={setIsSuccessDeleted}
                            setIsSuccessActivate={setIsSuccessActivate}
                            setIsSuccessDeletedMFA={setIsSuccessDeletedMFA}
                            setIsError={setIsError}
                            setIsErrorDGM={setIsErrorDGM}
                        />

                        <TableFooter
                            brand={brand}
                            content={dsmSearch.users}
                            setDisplayedContent={setDisplayedContent}
                        />
                    </>
                ) : (
                    <div className='no-content no-results'>
                        No results found. Please verify your search criteria and
                        try again
                    </div>
                )}
            </div>

            <div style={{ gridColumnStart: 2, marginTop: '40px' }}>
                <FooterPrivacy />
            </div>
        </div>
    );
};

export default UserSearch;
